import React, { useEffect, useState } from "react";
import { Button, Col, Form, Pagination, Row, Table, Tooltip } from "antd";

import { useAppDispatch, useAppSelector } from "../..";
import {
  fetchRoutingPlanesSearch,
  fetchRoutingPlanesVersions,
} from "../../store/slices/routingPlanes/routingPlanes";

import { ReactComponent as ArrowDetailsIcon } from "../../img/arrow-details.svg";
import { expandedTableEyeIcon } from "../../lib/icons";

import { ReactComponent as DotsIcon } from "../../img/3dots.svg";

import { SearchFilterRoutingPlans } from "./SearchFilterRoutingPlans";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import "./RoutingPlans.less";
import { AnyObject } from "../../lib/interfaces";
import {
  IRoutingPlan,
  IRoutingPlanDetails,
  IRoutingPlanVersion,
  IRoutingPlanVersionsList,
} from "../../store/slices/routingPlanes/types/routingPlanes";
import { checkIsLoading } from "../../lib/utils";
import { RoutingPlanSetting } from "./RoutingPlanSetting";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../RolesModule/hasAccess";

const pageSizeOptions = {
  10: 10,
  50: 50,
  100: 100,
  1000: 1000,
};

const columnRoutingPlansDetailsWidth = {
  proportionRatio: "10%",
  organization: "250px",
  offeringName: "",
  rateMax: "10%",
  currencyName: "15%",
  actions: "430px",
};

const columnRoutingOrdersWidth = {
  destination: "20%",
  abbrev: "10%",
  offeringName: "15%",
  categoryId: "15%",
  orderStatus: "10%",
  datePeriod: "",
  buttons: "50px",
};

interface IModal {
  flag: string;
  payload: AnyObject;
}

type ScreenType = "list" | "details";

export const RoutingPlans = ({ dicCurrencies, offerList, destinationCategories }) => {
  const initialStateFilters = {
    page: 0,
    size: 10,
    offeringId: offerList.length ? offerList[0].id : 0,
    currencyId: 4,
  };

  const dispatch = useAppDispatch();
  const [advancedSearchForm] = Form.useForm();
  const {
    routingPlanesList,
    routingOrdersTotalElements,
    routingPlanesListLoading,
    routingPlanesVersions,
  } = useAppSelector((state) => state.routingPlanes);
  const [screen, setScreen] = useState<ScreenType>("list");
  const [openPlanToEdit, setOpenPlanToEdit] = useState<IRoutingPlan | undefined>(undefined);
  const [dataSource, setDataSource] = useState<IRoutingPlan[]>([]);
  const [dataSourceVersions, setDataSourceVersions] = useState<IRoutingPlanVersionsList[]>([]);
  const [objectHistory, setObjectHistory] = useState<IRoutingPlan | undefined>(undefined);
  const [objectVersionHistory, setObjectVersionHistory] = useState<IRoutingPlanVersion | undefined>(
    undefined
  );

  const showHistoryModal = (record: IRoutingPlan) => {
    setObjectHistory(record);
  };

  const showVersionHistoryModal = (record: IRoutingPlanVersion) => {
    setObjectVersionHistory(record);
  };

  // const [ modal, setModal ] = useState<IModal>({ flag: '', payload: {}})
  const [filters, setFilters] = useState(initialStateFilters);

  const [columnDetail, setColumnDetail] = useState([
    {
      title: "%",
      key: "proportionRatio",
      dataIndex: "proportionRatio",
      width: columnRoutingPlansDetailsWidth.proportionRatio,
    },
    {
      title: "Поставщик",
      key: "organization",
      dataIndex: "organization",
      width: columnRoutingPlansDetailsWidth.organization,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      width: columnRoutingPlansDetailsWidth.offeringName,
    },
    {
      title: "Цена",
      key: "rateMax",
      dataIndex: "rateMax",
      width: columnRoutingPlansDetailsWidth.rateMax,
    },
    {
      title: "Валюта",
      key: "currencyName",
      dataIndex: "currencyName",
      width: columnRoutingPlansDetailsWidth.currencyName,
    },
    {
      dataIndex: "actions",
      key: "actions",
      hidden: false,
      width: columnRoutingPlansDetailsWidth.actions,
      render: () => {
        return <div />;
      },
    },
  ]);

  const [columns, setColumns] = useState([
    {
      title: "Направление",
      dataIndex: "destination",
      key: "destination",
      hidden: false,
      width: columnRoutingOrdersWidth.destination,
      render: (dataIndex, record: IRoutingPlan) => {
        return (
          <div style={{ display: "flex" }}>
            <span style={{ paddingRight: "5px" }}>{record.destination}</span>
            <span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDetails(dataIndex, record);
                }}
                className={
                  record.isOpenDetails
                    ? "button-icon iconButton routingOrder-icon routingOrder-icon__rotate"
                    : "button-icon iconButton routingOrder-icon"
                }
                style={{ outline: "none", cursor: "pointer" }}
                icon={<ArrowDetailsIcon />}
              />
            </span>
          </div>
        );
      },
    },
    {
      title: "Аббревиатура",
      key: "abbrev",
      dataIndex: "abbrev",
      hidden: true,
      width: columnRoutingOrdersWidth.abbrev,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      hidden: false,
      width: columnRoutingOrdersWidth.offeringName,
    },
    {
      title: "Категория",
      key: "categoryId",
      dataIndex: "categoryId",
      hidden: false,
      width: columnRoutingOrdersWidth.categoryId,
      render: (_, record) => {
        let category = "";
        Array.isArray(destinationCategories) &&
          destinationCategories.forEach((item) => {
            if (item.id === record.categoryId) {
              category = item.name;
            }
          });
        return category;
      },
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      hidden: false,
      width: columnRoutingOrdersWidth.datePeriod,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnRoutingOrdersWidth.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  const handleToggleDetailsVersion = (dataIndex: any, record: IRoutingPlanVersion) => {
    setDataSourceVersions((prev) => {
      return prev.map((item) => {
        const list = item.list.map((elem) => {
          if (elem.id === record.id) {
            elem.isOpenDetails = !elem.isOpenDetails;
          }
          return elem;
        });
        return { ...item, list };
      });
    });
  };

  const [columnVersions, setColumnVersions] = useState([
    {
      title: "Направление",
      dataIndex: "destination",
      key: "destination",
      hidden: false,
      width: columnRoutingOrdersWidth.destination,
      render: (dataIndex, record: IRoutingPlanVersion) => {
        return (
          <div style={{ display: "flex" }}>
            <span style={{ paddingRight: "5px" }}>{record.destination}</span>
            <span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDetailsVersion(dataIndex, record);
                }}
                className={
                  record.isOpenDetails
                    ? "button-icon iconButton routingOrder-icon routingOrder-icon__rotate"
                    : "button-icon iconButton routingOrder-icon"
                }
                style={{ outline: "none", cursor: "pointer" }}
                icon={<ArrowDetailsIcon />}
              />
            </span>
          </div>
        );
      },
    },
    {
      title: "Аббревиатура",
      key: "abbrev",
      dataIndex: "abbrev",
      hidden: true,
      width: columnRoutingOrdersWidth.abbrev,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      hidden: false,
      width: columnRoutingOrdersWidth.offeringName,
    },
    {
      title: "Категория",
      key: "categoryId",
      dataIndex: "categoryId",
      hidden: false,
      width: columnRoutingOrdersWidth.categoryId,
      render: (_, record) => {
        let category = "";
        Array.isArray(destinationCategories) &&
          destinationCategories.forEach((item) => {
            if (item.id === record.categoryId) {
              category = item.name;
            }
          });
        return category;
      },
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      hidden: false,
      width: columnRoutingOrdersWidth.datePeriod,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnRoutingOrdersWidth.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      hidden: false,
      render: (value, record: IRoutingPlanVersion) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showVersionHistoryModal(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  useEffect(() => {
    setColumnVersions((prev) => {
      return prev.map((item) => {
        columns.forEach((column) => {
          if (item.dataIndex === column.dataIndex) {
            item.hidden = column.hidden || false;
          }
        });
        return item;
      });
    });
  }, [columns]);

  useEffect(() => {
    dispatch(fetchRoutingPlanesSearch(filters));
  }, [filters]);

  useEffect(() => {
    const _routingPlanesList: IRoutingPlan[] = routingPlanesList.map((item) => {
      return {
        ...item,
        routingItems: item?.routingItems.map((elem, idx) => {
          return {
            ...elem,
            currencyName: getCurrencyNameById(dicCurrencies, elem.currencyId),
            key: idx,
          };
        }),
      };
    });
    setDataSource(_routingPlanesList);
  }, [routingPlanesList, dicCurrencies]);

  useEffect(() => {
    setDataSourceVersions((prev) => {
      const _routingOrdersVersions: IRoutingPlanVersionsList[] = [...prev];
      routingPlanesVersions.forEach((item) => {
        let exist = false;
        prev.forEach((elem) => {
          if (item.id === elem.id) exist = true;
        });
        if (!exist) {
          _routingOrdersVersions.push({
            id: item.id,
            list: item.list.map((version) => {
              return { ...version };
            }),
          });
        }
      });
      return _routingOrdersVersions;
    });
  }, [routingPlanesVersions]);

  const getCurrencyNameById = (dicCurrencies, id) => {
    let name = "";
    Array.isArray(dicCurrencies) &&
      dicCurrencies.forEach((item) => {
        if (item.id === id) {
          name = item.name;
        }
      });
    return name;
  };

  const getTableDataVersions = (
    dataSourceVersions: IRoutingPlanVersionsList[],
    record: IRoutingPlan
  ): IRoutingPlanVersion[] => {
    const _dataSource: IRoutingPlanVersion[] = [];
    dataSourceVersions.forEach((item) => {
      if (item.id === record.tcOfferingItemId) {
        item.list.forEach((elem, idx) => {
          _dataSource.push({
            ...elem, // , key: idx
          });
        });
      }
    });
    return _dataSource;
  };

  const handleToggleDetails = (dataIndex, record: IRoutingPlan) => {
    setDataSource((prev) => {
      return prev.map((item, idx) => {
        return {
          ...item,
          isOpenDetails: record.id === item.id ? !item.isOpenDetails : item.isOpenDetails,
        };
      });
    });
  };

  const handleToggleVersion = async (record: IRoutingPlan) => {
    await dispatch(
      fetchRoutingPlanesVersions({
        tcOfferingItemId: record.tcOfferingItemId,
      })
    );
    setDataSource((prev) => {
      const _dataSource = prev.map((item) => {
        if (item.key === record.key) {
          return { ...item, isOpenVersions: !item.isOpenVersions };
        }
        return item;
      });
      return _dataSource;
    });
  };

  const getDetailsTableData = (record: IRoutingPlan): IRoutingPlanDetails[] => {
    return record.routingItems;
  };

  const getRowClassName = (record: IRoutingPlan, idx) => {
    if (record.isOpenDetails || record.isOpenVersions) {
      return "routingOrder-version-row routingOrder-version-row__extended";
    }
    return "";
  };

  const getExpandedRowKeys = (dataSource: IRoutingPlan[]) => {
    const keys: number[] = [];
    dataSource.forEach((item) => {
      if (item.isOpenDetails || item.isOpenVersions) {
        keys.push(item.key);
      }
    });
    return keys;
  };

  const expandedRowRender = (record: IRoutingPlan) => {
    return (
      <>
        {record.isOpenDetails && (
          <Table
            columns={columnDetail}
            dataSource={getDetailsTableData(record)}
            pagination={false}
            className="table_no-hover child-table"
            // style={{marginLeft: '-8px', marginRight: '-8px'}}
          />
        )}
        {record.isOpenVersions && (
          <Table
            columns={[...columnVersions].filter((item) => !item.hidden)}
            // columns={getColumnVersion(columns, columnVersions)}
            dataSource={getTableDataVersions(dataSourceVersions, record)}
            rowClassName={getVersionsRowClassName}
            pagination={false}
            showHeader={false}
            className="table_no-hover child-table"
            expandable={{
              expandedRowKeys: getExpandedVersionRowKeys(dataSourceVersions, record),
              expandedRowRender: expanderVersionsRowRender,
              expandIconColumnIndex: 6,
              columnWidth: "0px",
              indentSize: 0,
              expandIcon: () => {
                return undefined;
              },
            }}
          />
        )}
      </>
    );
  };

  const getExpandedVersionRowKeys = (
    dataSourceVersions: IRoutingPlanVersionsList[],
    record: IRoutingPlan
  ) => {
    const openKeys: number[] = [];
    dataSourceVersions.forEach((versions) => {
      if (versions.id === record.id) {
        versions.list.forEach((elem) => {
          if (elem.isOpenDetails) {
            openKeys.push(elem.key);
          }
        });
      }
    });
    return openKeys;
  };

  const getVersionDetailsTableData = (record: IRoutingPlanVersion) => {
    return record.routingItems;
  };

  const expanderVersionsRowRender = (record: IRoutingPlanVersion) => {
    return (
      <Table
        columns={columnDetail}
        dataSource={getVersionDetailsTableData(record)}
        pagination={false}
        className="table_no-hover child-table"
      />
    );
  };

  const getVersionsRowClassName = (record, idx) => {
    const classNames = ["routingOrder-version-row"];
    if (idx === 0) {
      classNames.push("routingOrder-version-row__active");
    }
    return classNames.join(" ");
  };

  return (
    <>
      <div className="container_height">
        {screen === "list" && (
          <>
            <div className="routingPlans">
              <Row justify="space-between" align="middle">
                <h1 className="main-title">Планы маршрутизации</h1>
              </Row>
              <SearchFilterRoutingPlans
                dicCurrencies={dicCurrencies}
                offerList={offerList}
                initialStateFilters={initialStateFilters}
                // dicRoutingOrdersStatuses={dicRoutingOrdersStatuses}
                advancedSearchForm={advancedSearchForm}
                // advancedSearchFormValues={advancedSearchFormValues}
                // loadRoutingOrdersList={loadRoutingOrdersList}
                setFilters={setFilters}
              />
            </div>

            <div className="routingPlans-table versions-table">
              <Row justify="space-between" align="middle" style={{ paddingBottom: 10 }}>
                <Col span={8} />
                <Col span={8} style={{ textAlign: "center" }}>
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <Pagination
                    size="small"
                    current={filters.page + 1}
                    total={routingOrdersTotalElements}
                    showSizeChanger={false}
                    onChange={(page: number) => {
                      setFilters((prev) => {
                        return { ...prev, page: page - 1 };
                      });
                    }}
                    pageSize={filters.size}
                  />
                </Col>
                <Col span={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <TablePageSelect
                    onChange={(e: any) =>
                      setFilters((prev) => {
                        return { ...prev, size: e, page: initialStateFilters.page };
                      })
                    }
                    value={filters.size}
                    pageSizeOptions={pageSizeOptions}
                  />
                </Col>
              </Row>

              <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                <ToggleColumns
                  columns={columns}
                  setColumns={setColumns}
                  // settings={settings}
                  // setSettings={setSettings}
                  tableName="routing-orders"
                  // setChildTableColumns={setChildTableColumns}
                />
              </Row>

              <Table
                size="small"
                className="table_no-hover"
                columns={[...columns].filter((item) => !item.hidden)}
                dataSource={dataSource}
                loading={checkIsLoading(routingPlanesListLoading)}
                pagination={false}
                // scroll={{ y: 400, x: 1200 }}
                rowClassName={(row, idx) => getRowClassName(row, idx)}
                onRow={(record, rowIdx) => {
                  return {
                    onClick: () => {
                      setScreen("details");
                      setOpenPlanToEdit(record);
                    },
                  };
                }}
                expandable={{
                  expandedRowKeys: getExpandedRowKeys(dataSource),
                  expandedRowRender,
                  expandIconColumnIndex: [...columns].filter((item) => !item.hidden).length - 1,
                  columnWidth: "50px",
                  indentSize: 0,
                  expandIcon: ({ expanded, record }) => {
                    return (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVersion(record);
                          }}
                          className="button-icon iconButton"
                          style={{ outline: "none", cursor: "pointer" }}
                          icon={expandedTableEyeIcon(record.isOpenVersions ? "#0E5298" : "#86ACD2")}
                        />
                      </div>
                    );
                  },
                }}
              />
            </div>
          </>
        )}

        {screen === "details" && (
          <>
            <RoutingPlanSetting
              columns={columns}
              getRowClassName={getRowClassName}
              openPlanToEdit={openPlanToEdit}
              columnDetail={columnDetail}
              dataSourceVersions={dataSourceVersions}
              getVersionsRowClassName={getVersionsRowClassName}
              tcOfferingItemId={openPlanToEdit?.tcOfferingItemId || undefined}
              destinationId={openPlanToEdit?.destinationId}
              currencyId={filters.currencyId}
              dicCurrencies={dicCurrencies}
              destinationCategories={destinationCategories}
              handleBack={() => setScreen("list")}
              setObjectVersionHistory={setObjectVersionHistory}
            />
          </>
        )}
      </div>

      <HistoryAndPropertyModal
        objectTypeSysname="tc_routing_order"
        objectId={objectHistory ? objectHistory.id : -1}
        handleHideModal={() => setObjectHistory(undefined)}
        visibleModal={!!objectHistory}
        dateObjectFromForCalendar={objectHistory ? objectHistory.fromDate : ""}
        dateObjectToForCalendar={objectHistory ? objectHistory.toDate : ""}
        hasAccessToEdit={false} // TODO добавить доспы к редактированию
      />

      <HistoryAndPropertyModal
        objectTypeSysname="tc_routing_order"
        objectId={objectVersionHistory ? objectVersionHistory.tcOfferingItemId : -1}
        handleHideModal={() => setObjectVersionHistory(undefined)}
        visibleModal={!!objectVersionHistory}
        dateObjectFromForCalendar={objectVersionHistory ? objectVersionHistory.fromDate : ""}
        dateObjectToForCalendar={objectVersionHistory ? objectVersionHistory.toDate : ""}
        hasAccessToEdit={false} // TODO добавить доспы к редактированию
      />
    </>
  );
};
